/* eslint-disable @typescript-eslint/no-non-null-assertion */

import type { Home } from '@lmc/ts-common'
import { EmptyState, NotFoundIcon } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { isBrowser, isTablet } from 'react-device-detect'
import { useSelector } from 'react-redux'
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate
} from 'react-router-dom'
import styled from 'styled-components'
import { type CustomMenuItemType, isGroupUser } from 'utils'
import { SettingsSidebarNavigation } from '../components'
import { getNavigationTabs } from '../utils/navigationTabs'

const StyledContainer = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  max-width: 2000px;
  width: 100%;
`

const StyledRightContainer = styled.div`
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  width: 100%;
  margin: 0 auto;
`

export const AccountsSettingsScreen = () => {
  const canManageHome = useAbilityAccess([{ i: 'update', this: 'Home' }])
  const canViewSkillsAndNeedsTemplates = useAbilityAccess([
    { i: 'view', this: 'SkillNeedTemplate' }
  ])
  // view UserInvitation is exclusive to careHomeAdmin among home-level roles (lmcAdmin only has update)
  const isCareHomeAdmin = useAbilityAccess([
    { i: 'view', this: 'UserInvitation' }
  ])
  const abilitiesLoaded = useSelector(
    (state: { global: { ability: any } }) => !!state.global.ability
  )
  const user = useSelector(
    (state: { global: { user: any } }) => state.global.user
  )
  const isGroupRole = isGroupUser(user?.role)
  const { hasRota } = useHomeType()
  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const navigate = useNavigate()

  const location = useLocation()

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_tolerance_settings',
      'travel_and_mileage',
      'geofencing',
      'mods_needs_settings',
      'payrates',
      'rota_invoicing',
      'emar_add_edit_medication_second_signature'
    ]
  })?.data
  const flagsRosteringToleranceSettings =
    flags?.rostering_tolerance_settings?.enabled

  const flagsTravelTimeAndMileageEnabled = flags?.travel_and_mileage?.enabled

  const flagsGeofencingEnabled = flags?.geofencing?.enabled

  const flagsModsNeedsSettings = flags?.mods_needs_settings?.enabled

  const flagsPayRatesEnabled = flags?.payrates?.enabled

  const flagsRotaInvoicingEnabled = flags?.rota_invoicing?.enabled

  const flagsEmarSecondSignatureEnabled =
    flags?.emar_add_edit_medication_second_signature?.enabled

  if (!abilitiesLoaded) {
    return <Spinner />
  }

  if (!canManageHome && !isGroupRole) {
    return (
      <EmptyState
        title="We cant find what you’re looking for... Error 404"
        description="Don’t worry, try refreshing the page and if the problem persists contact support."
        icon={<NotFoundIcon />}
      >
        <Link to="/">Go back home</Link>
      </EmptyState>
    )
  }

  const navigationKey =
    location.pathname !== '/settings'
      ? location?.pathname?.split('settings/')[1]
      : '/reports/billing'

  return (
    <StyledContainer>
      {isDesktop && (
        <SettingsSidebarNavigation
          navigationKey={navigationKey}
          navigate={navigate}
          menuItems={
            getNavigationTabs(
              features,
              isGroupRole,
              isCareHomeAdmin,
              hasRota,
              flagsRosteringToleranceSettings,
              flagsTravelTimeAndMileageEnabled,
              flagsGeofencingEnabled,
              flagsModsNeedsSettings,
              flagsPayRatesEnabled,
              canViewSkillsAndNeedsTemplates,
              flagsRotaInvoicingEnabled,
              flagsEmarSecondSignatureEnabled
            ) as CustomMenuItemType[]
          }
        />
      )}
      <StyledRightContainer>
        {location.pathname === '/settings' ? (
          <Navigate to={isGroupRole ? 'skills-and-needs' : 'billing'} />
        ) : location.pathname === '/settings/skills-and-needs' &&
          flagsModsNeedsSettings ? (
          <Navigate to="skills-and-needs/skill-templates" replace />
        ) : location.pathname.startsWith('/settings/skills-and-needs/') &&
          flagsModsNeedsSettings === false ? (
          <Navigate to="/settings/skills-and-needs" replace />
        ) : (
          <Outlet />
        )}
      </StyledRightContainer>
    </StyledContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default AccountsSettingsScreen
